
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Zap, Brain, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const OPENAI_MODELS = [
  {
    value: 'gpt-4o-mini',
    label: 'GPT-4o Mini',
    description: 'Rask og kostnadseffektiv modell for de fleste oppgaver',
    icon: <Zap className="h-4 w-4" />,
    badge: 'Rask & Rimelig',
    badgeVariant: 'secondary' as const,
    features: [
      'Rask respons',
      'Lavere kostnader',
      'God for enkle spørsmål',
      'Støtter bilder'
    ]
  },
  {
    value: 'gpt-4o',
    label: 'GPT-4o',
    description: 'Kraftig modell for komplekse analyser og arbeidsmarkedsprediksjoner',
    icon: <Brain className="h-4 w-4" />,
    badge: 'Kraftig & Presis',
    badgeVariant: 'default' as const,
    features: [
      'Avansert analyse',
      'Bedre kvalitet',
      'Komplekse oppgaver',
      'Støtter bilder'
    ]
  }
];

export function ModelSelector() {
  const [selectedModel, setSelectedModel] = useState('');
  const [originalModel, setOriginalModel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadCurrentModel();
  }, []);

  useEffect(() => {
    setHasChanges(selectedModel !== originalModel);
  }, [selectedModel, originalModel]);

  const loadCurrentModel = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'openai_model')
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const modelValue = data?.setting_value || 'gpt-4o';
      setSelectedModel(modelValue);
      setOriginalModel(modelValue);
    } catch (error: any) {
      console.error('Error loading model setting:', error);
      setError('Kunne ikke laste modellinnstilling');
      toast({
        title: "Feil ved lasting",
        description: "Kunne ikke laste gjeldende modellinnstilling",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveModel = async () => {
    try {
      setSaving(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('system_settings')
        .update({
          setting_value: selectedModel,
          updated_by: (await supabase.auth.getUser()).data.user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'openai_model');

      if (updateError) {
        throw updateError;
      }

      // Logg endringen i audit_log
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('audit_log')
          .insert({
            user_id: user.id,
            action: 'UPDATE',
            table_name: 'system_settings',
            record_id: 'openai_model',
            old_values: { setting_value: originalModel },
            new_values: { setting_value: selectedModel }
          });
      }

      setOriginalModel(selectedModel);
      toast({
        title: "Modell oppdatert",
        description: `Klara bruker nå ${OPENAI_MODELS.find(m => m.value === selectedModel)?.label}`,
      });
    } catch (error: any) {
      console.error('Error saving model:', error);
      setError('Kunne ikke lagre modellvalg');
      toast({
        title: "Feil ved lagring",
        description: "Kunne ikke lagre modellvalget",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setSelectedModel(originalModel);
    setError(null);
  };

  const selectedModelInfo = OPENAI_MODELS.find(m => m.value === selectedModel);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Laster modellinnstillinger...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              OpenAI Språkmodell
              {hasChanges && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  Ulagrede endringer
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Velg hvilken OpenAI-modell Klara skal bruke for å svare på spørsmål
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={resetChanges}
              disabled={!hasChanges || saving}
            >
              Tilbakestill
            </Button>
            <Button
              onClick={saveModel}
              disabled={!hasChanges || saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Velg språkmodell</label>
            <Select value={selectedModel} onValueChange={setSelectedModel} disabled={saving}>
              <SelectTrigger>
                <SelectValue placeholder="Velg en modell" />
              </SelectTrigger>
              <SelectContent>
                {OPENAI_MODELS.map(model => (
                  <SelectItem key={model.value} value={model.value}>
                    <div className="flex items-center gap-2">
                      {model.icon}
                      <span>{model.label}</span>
                      <Badge variant={model.badgeVariant} className="text-xs">
                        {model.badge}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedModelInfo && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                {selectedModelInfo.icon}
                <h3 className="font-medium">{selectedModelInfo.label}</h3>
                <Badge variant={selectedModelInfo.badgeVariant}>
                  {selectedModelInfo.badge}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {selectedModelInfo.description}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {selectedModelInfo.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-1 text-xs">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {hasChanges && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Du har valgt en ny modell. Husk å lagre endringene for at de skal tre i kraft.
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded-lg">
          <strong>Tips:</strong> GPT-4o Mini er raskere og billigere for enkle spørsmål, 
          mens GPT-4o gir bedre kvalitet for komplekse arbeidsmarkedsanalyser og prediksjoner.
        </div>
      </CardContent>
    </Card>
  );
}
